import type { Locale, Translation } from './types';

// Core translations that are shared across all portals
// These are common UI elements, actions, and form labels
export const coreTranslations: Record<Locale, Translation> = {
  en: {
    appearance: {
      title: 'Appearance',
      subtitle: 'Choose how the portal looks and what it shows you.',
      display: {
        title: 'Display',
        description: 'Theme and visual style.',
        theme: 'Theme',
        themeDesc: 'Select a visual theme for the portal.',
        light: 'Light',
        dark: 'Dark',
      },
      dashboards: {
        title: 'Dashboards',
        description: 'What the dashboard charts show.',
        hideFridays: 'Hide Fridays',
        hideFridaysDesc: 'Leave Fridays out of the daily dashboard charts. Totals always include them.',
      },
    },
    common: {
      // Actions
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      close: 'Close',
      export: 'Export',
      share: 'Share',
      sharing: 'Sharing...',
      shareFileFailed: 'Unable to share file on this device.',
      retry: 'Try Again',
      loading: 'Loading...',
      search: 'Search',
      filter: 'Filter',

      // Status
      all: 'All',
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',

      // Time
      today: 'Today',
      yesterday: 'Yesterday',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      thisYear: 'This Year',
      lastYear: 'Last Year',

      // Messages
      error: 'Error',
      success: 'Success',
      comingSoon: 'Coming Soon',
      optional: 'Optional',
      viewAll: 'View All',
      total: 'Total',
      noResults: 'No results found',
      actions: 'Actions',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      create: 'Create',
      update: 'Update',
      add: 'Add',
      remove: 'Remove',
      select: 'Select',
      clear: 'Clear',
      reset: 'Reset',
      apply: 'Apply',
      done: 'Done',
      min: 'Min',
      max: 'Max',
      noOptionsFound: 'No options found',
      saveChanges: 'Save Changes',
      unknown: 'Unknown',
      copied: 'Copied!',
      copy: 'Copy',
      creating: 'Creating...',
      notAvailable: 'N/A',
      overview: 'Overview',
      other: 'Other',
      view: 'View',
      filters: 'Filters',
      filtersActive: 'filters active',
      tenant: 'Tenant',
      period: 'Period',
      ascending: 'Ascending',
      descending: 'Descending',
      sort: 'Sort',
      clearAll: 'Clear all',
      clearFilters: 'Clear filters',
      noOptions: 'No options available',
      from: 'From',
      to: 'To',
      noLimit: 'No limit',
      transactions: 'Transactions',
      noTransactions: 'No transactions found',
      user: 'User',
      guest: 'Guest',
      processing: 'Processing...',
      date: 'Date',
      yes: 'Yes',
      no: 'No',
      selectDateRange: 'Select date range',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      pendingChanges: 'pending changes',
      discard: 'Discard',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    },

    // Shared validation messages (used by driver drawer, forms, etc.)
    validation: {
      phoneSaudiFormat:
        'Enter 9 digits (no leading 0) or 10 digits (with 0). Saudi format: +966.',
      phoneRequired: 'Phone number is required.',
      driverIdMustBe10Digits: 'Driver ID must be exactly 10 digits.',
      driverIdRequired: 'Driver ID is required.',
      driverIdFormat: 'Driver ID must be exactly 10 digits.',
    },

    // Overview/Dashboard section
    overview: {
      welcome: 'Welcome back',
      summary: "Here's what's happening with your account today",
      recentOrders: 'Recent Orders',
      recentActivity: 'Your latest order activity',
      viewAll: 'View All',
      contractUsage: 'Contract Usage',
      utilizationProgress: 'Utilization progress for active contracts',
      yearlyTrend: 'Yearly Trend',
    },

    // Stats section
    stats: {
      activeContracts: 'Active Contracts',
      totalOrders: 'Total Orders',
      productsDelivered: 'Products Delivered',
      savings: 'Savings',
      thisMonth: 'this month',
      thisYear: 'this year',
      vsLastMonth: 'vs last month',
    },

    form: {
      name: 'Name',
      nameEn: 'Name (English)',
      nameAr: 'Name (Arabic)',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      city: 'City',
      country: 'Country',
      selectCountry: 'Select country',
      selectCity: 'Select city',
      postalCode: 'Postal Code',
      description: 'Description',
      notes: 'Notes',
      status: 'Status',

      validation: {
        required: 'This field is required',
        emailInvalid: 'Please enter a valid email address',
        phoneInvalid: 'Please enter a valid phone number',
        minLength: 'Minimum length not met',
        maxLength: 'Maximum length exceeded',
      },
    },

    dateRangePicker: {
      placeholder: 'Select date range',
      presetsLabel: 'Quick Select',
      startLabel: 'Start',
      endLabel: 'End',
      clearLabel: 'Reset selection',
      selectDatePlaceholder: 'Select date',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      thisQuarter: 'This Quarter',
      thisYear: 'This Year',
      next30Days: 'Next 30 Days',
      next90Days: 'Next 90 Days',
    },

    toast: {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Info',
      employees: {
        created: 'Employee created successfully',
        updated: 'Employee updated successfully',
        deleted: 'Employee deleted successfully',
        fetchFailed: 'Failed to fetch employees',
        createFailed: 'Failed to create employee',
        updateFailed: 'Failed to update employee',
        deleteFailed: 'Failed to delete employee',
      },
      departments: {
        created: 'Department created successfully',
        updated: 'Department updated successfully',
        deleted: 'Department deleted successfully',
        fetchFailed: 'Failed to fetch departments',
        createFailed: 'Failed to create department',
        updateFailed: 'Failed to update department',
        deleteFailed: 'Failed to delete department',
      },
      visitors: {
        created: 'Visit created successfully',
        checkedIn: 'Visitor checked in successfully',
        checkedOut: 'Visitor checked out successfully',
        approved: 'Visit approved successfully',
        rejected: 'Visit rejected successfully',
        cancelled: 'Visit cancelled successfully',
        fetchFailed: 'Failed to fetch visits',
        createFailed: 'Failed to create visit',
        checkInFailed: 'Failed to check in visitor',
        checkOutFailed: 'Failed to check out visitor',
        approveFailed: 'Failed to approve visit',
        cancelFailed: 'Failed to cancel visit',
      },
      cameras: {
        created: 'Camera created successfully',
        updated: 'Camera updated successfully',
        deleted: 'Camera deleted successfully',
        fetchFailed: 'Failed to fetch cameras',
      },
      addresses: {
        created: 'Address created successfully',
        updated: 'Address updated successfully',
        deleted: 'Address deleted successfully',
        favoriteAdded: 'Address added to favorites',
        favoriteRemoved: 'Address removed from favorites',
        setDefault: 'Default address updated',
        fetchFailed: 'Failed to fetch addresses',
        createFailed: 'Failed to create address',
        updateFailed: 'Failed to update address',
        deleteFailed: 'Failed to delete address',
      },
      accommodations: {
        roomTypeCreated: 'Room type created successfully',
        roomTypeUpdated: 'Room type updated successfully',
        roomTypeDeleted: 'Room type deleted successfully',
        roomCreated: 'Room created successfully',
        roomUpdated: 'Room updated successfully',
        roomDeleted: 'Room deleted successfully',
        bookingCreated: 'Room booking created successfully',
        bookingCancelled: 'Room booking cancelled',
        mealOptionCreated: 'Meal option created successfully',
        mealOptionUpdated: 'Meal option updated successfully',
        mealOptionDeleted: 'Meal option deleted successfully',
        approved: 'Accommodation approved',
        rejected: 'Accommodation rejected',
        fetchFailed: 'Failed to fetch accommodation data',
        createFailed: 'Failed to create accommodation',
        updateFailed: 'Failed to update accommodation',
        deleteFailed: 'Failed to delete accommodation',
        approveFailed: 'Failed to approve accommodation',
        rejectFailed: 'Failed to reject accommodation',
      },
      incomingMaterials: {
        created: 'Created successfully',
        updated: 'Shipment updated successfully',
        updateFailed: 'Failed to update shipment',
        sentToAds: 'Purchase order sent to ADS',
      },
    },

    header: {
      // Factory selector
      selectFactory: 'Select Factory',
      allFactories: 'All Factories',
      searchFactories: 'Search factories...',
      // Search
      search: 'Search contracts, orders...',
      // User
      customer: 'Customer',
      admin: 'Admin',
      profile: 'Profile',
      settings: 'Settings',
      logout: 'Logout',
    },

    // Contracts (used in sales orders and overview)
    contracts: {
      totalValue: 'Total Value',
      used: 'Used',
      remaining: 'Remaining',
      utilization: 'Utilization',
      lowStock: 'Low Stock',
      draft: 'Draft',
      expiringSoon: 'Expiring Soon',
      searchPlaceholder: 'Search contracts...',
      startDate: 'Start Date',
      endDate: 'End Date',
      orders: 'Orders',
      products: 'Products',
      status: 'Status',
      active: 'Active',
      completed: 'Completed',
      canceled: 'Canceled',
      duration: 'Duration',
      notes: 'Notes',
      createdBy: 'Created By',
    },

    sidebar: {
      // Main navigation
      overview: 'Overview',
      dashboard: 'Dashboard',
      menu: 'Menu',
      portal: 'Customer Portal',
      logout: 'Logout',
      help: 'Help',

      // Sales & Fulfillment
      salesFulfillment: 'Sales & Fulfillment',
      orderManagement: 'Order Management',
      salesOrders: 'Sales Orders',
      orders: 'Orders',
      customers: 'Customers',
      fulfillment: 'Fulfillment',
      contracts: 'Contracts',
      shipments: 'Shipments',
      incomingMaterials: 'Incoming Materials',
      logistics: 'Logistics',
      procurement: 'Procurement',

      // Sales
      sales: 'Sales',
      salesDashboard: 'Sales Dashboard',
      subCustomersDashboard: 'Sub-Customers Dashboard',
      customersDashboard: 'Customers Dashboard',
      salesTargets: 'Monthly Targets',

      // Catalog
      catalog: 'Catalog',
      products: 'Products',
      pricing: 'Pricing',
      availability: 'Availability',
      certificates: 'Certificates',

      // Finance
      finance: 'Finance',
      financeOverview: 'Finance Overview',
      statement: 'Statement',
      transactions: 'Transactions',
      payments: 'Payments',
      invoices: 'Invoices',
      customerAccounts: 'Customer Accounts',

      // Fleet
      fleet: 'Fleet',
      assets: 'Assets',
      resources: 'Resources',
      drivers: 'Drivers',
      vehicles: 'Vehicles',
      trailers: 'Trailers',

      // Locations
      locations: 'Locations',
      factories: 'Factories',
      addressbook: 'Address Book',
      projects: 'Projects',
      regions: 'Regions',
      cities: 'Cities',
      storageLocations: 'Storage Locations',

      // Gate Operations
      gateOperations: 'Gate Operations',
      gates: 'Gates',
      zones: 'Zones',
      loadingLocations: 'Loading Locations',
      visitors: 'Visitors',
      cameras: 'Cameras',
      cameraSpeed: 'Camera Speed',
      qrReaders: 'QR Readers',
      accessLogs: 'Access Logs',
      departments: 'Departments',
      employees: 'Employees',
      accommodations: 'Accommodations',
      roomTypes: 'Room Types',
      mealOptions: 'Meal Options',

      // Organization
      organization: 'Organization',
      tenants: 'Tenants',
      users: 'Users',

      // System
      system: 'System',
      systemAdmin: 'System Administration',
      adminDashboard: 'Admin Dashboard',
      systemHealth: 'System Health',
      settings: 'Settings',
      sapSync: 'SAP Sync',
      sapConfiguration: 'SAP Configuration',
      visitGuidelines: 'Visit Guidelines',
      simulation: 'Simulation',
      controlTower: 'Control Tower',
      yard: 'Yard Map',
      notificationTemplates: 'Notification Templates',
      hardwareLogs: 'Hardware Logs',
      portalConfig: 'Portal Configuration',
      support: 'Support',
      supportTickets: 'Support Tickets',

      // Secondary
      faq: 'FAQ',

      // Kiosk
      scan: 'Scan Order',
      weighbridge: 'Weighbridge',

      // Accessibility
      closeMenu: 'Close menu',
    },

    // Error boundary
    errorBoundary: {
      title: 'Something went wrong',
      description: "We're sorry, but something unexpected happened. Please try refreshing the page or going back to the homepage.",
      refreshPage: 'Refresh Page',
      goHome: 'Go Home',
      developerInfo: 'Developer Info',
      persistContact: 'If this problem persists, please contact',
    },

    // SSO / Handoff
    sso: {
      signingIn: 'Signing you in...',
      signInFailed: 'Sign-in Failed',
      goToLogin: 'Go to Login',
    },

    // Privacy Policy
    privacyPolicy: {
      backToSignIn: 'Back to Sign In',
      title: 'Privacy Policy',
      lastUpdated: 'Last updated:',
    },

    // Fleet Management
    fleet: {
      title: 'Fleet Management',
      subtitle: 'Manage drivers, vehicles, and resource assignments',
      resources: 'Resources',
      drivers: 'Drivers',
      vehicles: 'Vehicles',
      trailers: 'Trailers',
      resourcesSubtitle: 'Driver and vehicle assignments',
      driversSubtitle: 'Manage your drivers',
      vehiclesSubtitle: 'Manage your vehicle fleet',
      trailersSubtitle: 'Manage your trailers',
      search: 'Search...',

      // Resources
      addResource: 'Assign Resource',
      editResource: 'Edit Assignment',
      noResources: 'No resource assignments found',
      noResourcesDescription:
        'Get started by creating your first resource assignment.',
      assignDriver: 'Assign Driver',
      assignVehicle: 'Assign Vehicle',
      assignTrailer: 'Assign Trailer',
      assignedSince: 'Assigned Since',
      currentShipment: 'Current Shipment',
      resourceStatus: 'Status',
      resourceActive: 'Active',
      resourceOnDelivery: 'On Delivery',
      resourceReturning: 'Returning',
      resourceInactive: 'Inactive',
      unassign: 'Unassign',
      totalResources: 'Total Resources',
      activeResources: 'Active Resources',
      onDelivery: 'On Delivery',
      resourceName: 'Resource Name',
      resourceDescription: 'Description',

      // Drivers
      addDriver: 'Add Driver',
      editDriver: 'Edit Driver',
      deleteDriver: 'Delete Driver',
      noDrivers: 'No drivers found',
      noDriversDescription:
        'Get started by adding your first driver to the fleet.',
      driverName: 'Driver Name',
      driverNameAr: 'Driver Name (Arabic)',
      employeeId: 'Employee ID',
      licenseNumber: 'License Number',
      licenseType: 'License Type',
      licenseExpiry: 'License Expiry',
      selectDate: 'Select date',
      licenseExpiryRequired: 'License expiry is required.',
      licenseHeavy: 'Heavy Vehicle',
      licenseLight: 'Light Vehicle',
      licenseHazmat: 'Hazmat',
      licenseSpecial: 'Special',
      phone: 'Phone',
      driverStatus: 'Status',
      driverAvailable: 'Available',
      driverAssigned: 'Assigned',
      driverOnLeave: 'On Leave',
      driverInactive: 'Inactive',
      totalDrivers: 'Total Drivers',
      availableDrivers: 'Available',
      assignedDrivers: 'Assigned',
      photo: 'Photo',

      // Vehicles
      addVehicle: 'Add Vehicle',
      editVehicle: 'Edit Vehicle',
      deleteVehicle: 'Delete Vehicle',
      noVehicles: 'No vehicles found',
      noVehiclesDescription:
        'Get started by adding your first vehicle to the fleet.',
      plateNumber: 'License Plate',
      vehicleType: 'Vehicle Type',
      typeTruck: 'Truck',
      typeMixer: 'Mixer',
      typeTanker: 'Tanker',
      typeFlatbed: 'Flatbed',
      typeCrane: 'Crane',
      typeLoader: 'Loader',
      make: 'Make',
      model: 'Model',
      makeModel: 'Make / Model',
      year: 'Year',
      vin: 'VIN',
      capacity: 'Capacity',
      capacityUnit: 'Unit',
      tons: 'Tons',
      cubicMeters: 'm³',
      vehicleStatus: 'Status',
      vehicleAvailable: 'Available',
      vehicleAssigned: 'Assigned',
      vehicleMaintenance: 'Maintenance',
      vehicleOutOfService: 'Out of Service',
      ownership: 'Ownership',
      owned: 'Owned',
      leased: 'Leased',
      rentedOut: 'Rented Out',
      outsourced: 'Outsourced',
      brokered: 'Brokered',
      shared: 'Shared',
      subcontracted: 'Subcontracted',
      lastMaintenance: 'Last Maintenance',
      nextMaintenance: 'Next Maintenance',
      configureColumns: 'Columns',
      selectVisibleColumns: 'Select visible columns',
      totalVehicles: 'Total Vehicles',
      availableVehicles: 'Available',
      inMaintenance: 'In Maintenance',

      // Trailers
      addTrailer: 'Add Trailer',
      editTrailer: 'Edit Trailer',
      deleteTrailer: 'Delete Trailer',
      noTrailers: 'No trailers found',
      noTrailersDescription:
        'Get started by adding your first trailer to the fleet.',
    },

    // Admin Dashboard
    admin: {
      dashboard: 'Admin Dashboard',
      dashboardDescription: 'Central management hub for all South Gate operations',
      stats: {
        tenants: 'Tenants',
        activeTenants: 'Active organizations',
        users: 'Users',
        registeredUsers: 'Registered users',
        orders: 'Orders',
        totalOrders: 'Total orders',
        revenue: 'Revenue',
        monthlyRevenue: 'This month',
      },
      system: {
        fleetStatus: 'Fleet Status',
        activeVehicles: 'Active vehicles',
        inTransit: 'In Transit',
        atFactory: 'At Factory',
        maintenance: 'Maintenance',
        gateOperations: 'Gate Operations',
        activeGates: 'Active gates',
        todayEntries: "Today's Entries",
        todayExits: "Today's Exits",
        pendingVisitors: 'Pending Visitors',
        systemHealth: 'System Health',
        uptime: 'Uptime this month',
        apiLatency: 'API Latency',
        dbHealth: 'Database',
        cacheHitRate: 'Cache Hit Rate',
      },
      quickActions: 'Quick Actions',
      quickActionsDescription: 'Common administrative tasks',
      actions: {
        newTenant: 'Add Tenant',
        newUser: 'Add User',
        newProduct: 'Add Product',
        newVehicle: 'Add Vehicle',
      },
      availability: {
        matrix: 'Availability Matrix',
        factory: 'Factory',
        totalFactories: 'Factories',
        totalVariants: 'Variants',
        available: 'Available',
        unavailable: 'Unavailable',
        coverage: 'Coverage',
        coverageDesc: 'of total combinations',
        noData: 'No factories or variants available',
      },
      // Tenant Management
      tenantManagement: 'Tenant Management',
      tenantManagementDesc: 'Manage organizations and their access',
      tenantManagementSubtitle: 'Manage organizations and their access',
      createTenant: 'Create Tenant',
      totalTenants: 'Total Tenants',
      pendingApproval: 'Pending Approval',
      activeTenants: 'Active Tenants',
      suspendedTenants: 'Suspended',
      searchTenants: 'Search tenants...',
      noTenantsFound: 'No tenants found',
      noTenantsFoundDesc: 'Try adjusting your search or filter criteria',
      viewDetails: 'View Details',
      edit: 'Edit',
      approve: 'Approve',
      approveForbidden: "You don't have permission to approve customers. Ask your admin to add the permission.",
      suspend: 'Suspend',
      activate: 'Activate',
      active: 'Active',
      pending: 'Pending',
      suspended: 'Suspended',
      inactive: 'Inactive',
      b2b: 'B2B',
      ecommerce: 'E-commerce',
      status: 'Status',
      channel: 'Channel',
      tenants: {
        columns: {
          name: 'Tenant',
          channel: 'Channel',
          customerGroup: 'Customer Group',
          users: 'Users',
          status: 'Status',
          created: 'Created',
        },
      },
      // User Management
      userManagement: 'User Management',
      userManagementSubtitle: 'Manage user accounts and permissions',
      totalUsers: 'Total Users',
      activeUsers: 'Active Users',
      invitedUsers: 'Invited',
      blockedUsers: 'Blocked',
      inactiveUsers: 'Inactive',
      searchUsers: 'Search users...',
      users: {
        title: 'User Management',
        subtitle: 'Manage users across all tenants',
        subtitleTenant: 'Manage users in your tenant',
        addUser: 'Add User',
        addUserSuccess: 'User created successfully',
        addUserFailed: 'Failed to create user',
        columns: {
          name: 'Name',
          tenant: 'Tenant',
          roleLabel: 'Role',
          status: 'Status',
          created: 'Created',
          lastLogin: 'Last login',
        },
        neverLoggedIn: 'Never',
      },
      blocked: 'Blocked',
      noUsersFound: 'No users found',
      noUsersFoundDesc: 'Try adjusting your search or filter criteria',
      editUser: 'Edit User',
      username: 'Username',
      email: 'Email',
      phone: 'Phone',
      organizationInfo: 'Organization',
      companyName: 'Company',
      roleManagement: 'Role',
      activity: 'Activity',
      lastActive: 'Last active',
      emailConfirmed: 'Email confirmed',
      sendPasswordReset: 'Send Password Reset Email',
      resetPassword: 'Reset Password',
      filterByStatus: 'Filter by Status',
      filterByRole: 'Filter by Role',
      filterByLastLogin: 'Filter by Last Login',
      lastLoginFilter: {
        last7Days: 'Last 7 days',
        last30Days: 'Last 30 days',
        over30Days: 'Not in 30+ days',
        never: 'Never logged in',
      },
      inactiveUser: 'Deactivate User',
      reactivate: 'Reactivate',
      blockUser: 'Block User',
      unblockUser: 'Unblock User',
      roles: {
        superAdmin: 'Super Admin',
        admin: 'Admin',
        financeAdmin: 'Finance Admin',
        tenantAdmin: 'Tenant Admin',
        tenantUser: 'Tenant User',
        salesOfficeUser: 'Sales Office User',
        securityGuard: 'Security Guard',
        safetyManager: 'Safety Manager',
        servicesManager: 'Services Manager',
        kiosk: 'Kiosk',
        employee: 'Employee',
        customer: 'Customer',
        customerOperator: 'Customer Operator',
        scaleOperator: 'Scale Operator',
        scaleAdmin: 'Scale Admin',
        packhouseManager: 'Packhouse Manager',
        executive: 'Executive',
        headOfSales: 'Head of Sales',
        salesManager: 'Sales Manager',
        securityManager: 'Security Manager',
        it: 'IT',
        visitorApprovalAdmin: 'Visitor Approval Admin',
        scmAdmin: 'SCM Admin',
        authenticated: 'Authenticated',
        public: 'Public',
        ecommerce: 'E-commerce',
        manager: 'Manager',
        operator: 'Operator',
        viewer: 'Viewer',
      },
      user: {
        form: {
          addTitle: 'Add User',
          addDescription: 'Create a user account and assign access',
          editDescription: 'Update user profile and access settings',
          createUser: 'Create User',
          userInfo: 'User Information',
          regenerate: 'Regenerate',
          roleAssignment: 'Role & Assignment',
          selectRole: 'Select role',
          name: 'Full Name',
          email: 'Email Address',
          password: 'Password',
          phone: 'Phone Number',
          roleLabel: 'Role',
          plant: 'Plant',
          department: 'Department',
          tenant: 'Tenant',
          namePlaceholder: 'Enter full name',
          emailPlaceholder: 'user@example.com',
          passwordPlaceholder: 'Enter password',
          phonePlaceholder: '512345678',
          selectPlant: 'Select plant',
          allPlants: 'All Plants',
          departmentPlaceholder: 'Enter department',
          validation: {
            nameRequired: 'Name is required',
            emailRequired: 'Email is required',
            emailInvalid: 'Enter a valid email address',
            passwordRequired: 'Password is required',
            passwordMin: 'Password must be at least 6 characters',
            roleRequired: 'Role is required',
            plantRequiredForRole: 'Plant is required for the selected role',
          },
          role: {
            kiosk: 'Kiosk',
            salesOfficeUser: 'Sales Office User',
            financeAdmin: 'Finance Admin',
            securityGuard: 'Security Guard',
            safetyManager: 'Safety Manager',
            servicesManager: 'Services Manager',
            customerOperator: 'Customer Operator',
            scaleOperator: 'Scale Operator',
            scaleAdmin: 'Scale Admin',
            packhouseManager: 'Packhouse Manager',
            scmAdmin: 'SCM Admin',
          },
        },
      },
    },

    // Pagination
    pagination: {
      showing: 'Showing {{start}}-{{end}} of {{total}}',
      perPage: 'Per page',
      goToPage: 'Go to page {{page}}',
      previousPage: 'Previous page',
      nextPage: 'Next page',
    },

    // Timestamps
    timestamps: 'Timestamps',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    documentId: 'Document ID',

    // Additional common keys
    from: 'From',
    to: 'To',
    clearAll: 'Clear all',
    clearFilters: 'Clear filters',
    noOptions: 'No options available',
    shareUrl: 'Share URL',
    tableView: 'Table view',
    cardView: 'Card view',
    duplicate: 'Duplicate',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    presetRanges: 'Preset Ranges',

    shipments: {
      delivered: 'Delivered',
      loading: 'Loading',
      scheduled: 'Scheduled',
      quantity: 'Quantity',
      scheduledDate: 'Scheduled Date',
    },
    assets: {
      resourceManagement: 'Resource Management',
    },
  },

  ar: {
    appearance: {
      title: 'المظهر',
      subtitle: 'اختر شكل البوابة وما تعرضه لك.',
      display: {
        title: 'العرض',
        description: 'السمة والنمط المرئي.',
        theme: 'السمة',
        themeDesc: 'اختر سمة مرئية للبوابة.',
        light: 'فاتح',
        dark: 'داكن',
      },
      dashboards: {
        title: 'لوحات المعلومات',
        description: 'ما تعرضه الرسوم البيانية في لوحات المعلومات.',
        hideFridays: 'إخفاء أيام الجمعة',
        hideFridaysDesc: 'استبعاد أيام الجمعة من الرسوم البيانية اليومية. الإجماليات تشملها دائمًا.',
      },
    },
    common: {
      // Actions
      save: 'حفظ',
      saving: 'جاري الحفظ...',
      cancel: 'إلغاء',
      edit: 'تعديل',
      delete: 'حذف',
      close: 'إغلاق',
      export: 'تصدير',
      share: 'مشاركة',
      sharing: 'جارٍ المشاركة...',
      shareFileFailed: 'تعذر مشاركة الملف على هذا الجهاز.',
      retry: 'إعادة المحاولة',
      loading: 'جاري التحميل...',
      search: 'بحث',
      filter: 'تصفية',

      // Status
      all: 'الكل',
      active: 'نشط',
      inactive: 'غير نشط',
      pending: 'قيد الانتظار',

      // Time
      today: 'اليوم',
      yesterday: 'أمس',
      thisMonth: 'هذا الشهر',
      lastMonth: 'الشهر الماضي',
      thisYear: 'هذا العام',
      lastYear: 'العام الماضي',

      // Messages
      error: 'خطأ',
      success: 'نجح',
      comingSoon: 'قريباً',
      optional: 'اختياري',
      viewAll: 'عرض الكل',
      total: 'المجموع',
      noResults: 'لم يتم العثور على نتائج',
      actions: 'الإجراءات',
      confirm: 'تأكيد',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      submit: 'إرسال',
      create: 'إنشاء',
      update: 'تحديث',
      add: 'إضافة',
      remove: 'إزالة',
      select: 'اختيار',
      clear: 'مسح',
      reset: 'إعادة تعيين',
      apply: 'تطبيق',
      done: 'تم',
      min: 'الحد الأدنى',
      max: 'الحد الأقصى',
      noOptionsFound: 'لا توجد خيارات',
      saveChanges: 'حفظ التغييرات',
      unknown: 'غير معروف',
      copied: 'تم النسخ!',
      copy: 'نسخ',
      creating: 'جاري الإنشاء...',
      notAvailable: 'غير متاح',
      overview: 'نظرة عامة',
      other: 'أخرى',
      view: 'عرض',
      filters: 'الفلاتر',
      filtersActive: 'فلاتر نشطة',
      tenant: 'المستأجر',
      user: 'مستخدم',
      guest: 'ضيف',
      processing: 'جاري المعالجة...',
      date: 'التاريخ',
      yes: 'نعم',
      no: 'لا',
      selectDateRange: 'اختر نطاق التاريخ',
      last7Days: 'آخر 7 أيام',
      last30Days: 'آخر 30 يوم',
      period: 'الفترة',
      ascending: 'تصاعدي',
      descending: 'تنازلي',
      sort: 'ترتيب',
      clearAll: 'مسح الكل',
      clearFilters: 'مسح الفلاتر',
      noOptions: 'لا توجد خيارات متاحة',
      from: 'من',
      to: 'إلى',
      noLimit: 'بدون حد',
      transactions: 'المعاملات',
      noTransactions: 'لم يتم العثور على معاملات',
      pendingChanges: 'تغييرات معلقة',
      discard: 'تجاهل',
      createdAt: 'تاريخ الإنشاء',
      updatedAt: 'تاريخ التحديث',
    },

    // Shared validation messages (used by driver drawer, forms, etc.)
    validation: {
      phoneSaudiFormat:
        'أدخل 9 أرقام (بدون 0 في البداية) أو 10 أرقام (مع 0). التنسيق السعودي: +966.',
      phoneRequired: 'رقم الهاتف مطلوب.',
      driverIdMustBe10Digits: 'رقم الهوية يجب أن يكون 10 أرقام بالضبط.',
      driverIdRequired: 'رقم الهوية مطلوب.',
      driverIdFormat: 'رقم الهوية يجب أن يكون 10 أرقام بالضبط.',
    },

    // Overview/Dashboard section
    overview: {
      welcome: 'مرحباً بعودتك',
      summary: 'إليك ما يحدث في حسابك اليوم',
      recentOrders: 'الطلبات الأخيرة',
      recentActivity: 'أحدث نشاط للطلبات',
      viewAll: 'عرض الكل',
      contractUsage: 'استخدام العقود',
      utilizationProgress: 'تقدم الاستخدام للعقود النشطة',
      yearlyTrend: 'الاتجاه السنوي',
    },

    // Stats section
    stats: {
      activeContracts: 'العقود النشطة',
      totalOrders: 'إجمالي الطلبات',
      productsDelivered: 'المنتجات المسلمة',
      savings: 'التوفير',
      thisMonth: 'هذا الشهر',
      thisYear: 'هذا العام',
      vsLastMonth: 'مقارنة بالشهر الماضي',
    },

    form: {
      name: 'الاسم',
      nameEn: 'الاسم (بالإنجليزية)',
      nameAr: 'الاسم (بالعربية)',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      address: 'العنوان',
      city: 'المدينة',
      country: 'الدولة',
      selectCountry: 'اختر الدولة',
      selectCity: 'اختر المدينة',
      postalCode: 'الرمز البريدي',
      description: 'الوصف',
      notes: 'ملاحظات',
      status: 'الحالة',

      validation: {
        required: 'هذا الحقل مطلوب',
        emailInvalid: 'يرجى إدخال عنوان بريد إلكتروني صالح',
        phoneInvalid: 'يرجى إدخال رقم هاتف صالح',
        minLength: 'الحد الأدنى للطول غير مستوفى',
        maxLength: 'تم تجاوز الحد الأقصى للطول',
      },
    },

    dateRangePicker: {
      placeholder: 'اختر نطاق التاريخ',
      presetsLabel: 'اختيار سريع',
      startLabel: 'البداية',
      endLabel: 'النهاية',
      clearLabel: 'مسح',
      selectDatePlaceholder: 'اختر تاريخ',
      today: 'اليوم',
      thisWeek: 'هذا الأسبوع',
      thisMonth: 'هذا الشهر',
      last30Days: 'آخر 30 يوم',
      last90Days: 'آخر 90 يوم',
      thisQuarter: 'هذا الربع',
      thisYear: 'هذا العام',
      next30Days: 'الـ 30 يوم القادمة',
      next90Days: 'الـ 90 يوم القادمة',
    },

    toast: {
      success: 'نجح',
      error: 'خطأ',
      warning: 'تحذير',
      info: 'معلومات',
      employees: {
        created: 'تم إنشاء الموظف بنجاح',
        updated: 'تم تحديث الموظف بنجاح',
        deleted: 'تم حذف الموظف بنجاح',
        fetchFailed: 'فشل في جلب الموظفين',
        createFailed: 'فشل في إنشاء الموظف',
        updateFailed: 'فشل في تحديث الموظف',
        deleteFailed: 'فشل في حذف الموظف',
      },
      departments: {
        created: 'تم إنشاء القسم بنجاح',
        updated: 'تم تحديث القسم بنجاح',
        deleted: 'تم حذف القسم بنجاح',
        fetchFailed: 'فشل في جلب الأقسام',
        createFailed: 'فشل في إنشاء القسم',
        updateFailed: 'فشل في تحديث القسم',
        deleteFailed: 'فشل في حذف القسم',
      },
      visitors: {
        created: 'تم إنشاء الزيارة بنجاح',
        checkedIn: 'تم تسجيل دخول الزائر بنجاح',
        checkedOut: 'تم تسجيل خروج الزائر بنجاح',
        approved: 'تمت الموافقة على الزيارة بنجاح',
        rejected: 'تم رفض الزيارة بنجاح',
        cancelled: 'تم إلغاء الزيارة بنجاح',
        fetchFailed: 'فشل في جلب الزيارات',
        createFailed: 'فشل في إنشاء الزيارة',
        checkInFailed: 'فشل في تسجيل دخول الزائر',
        checkOutFailed: 'فشل في تسجيل خروج الزائر',
        approveFailed: 'فشل في الموافقة على الزيارة',
        cancelFailed: 'فشل في إلغاء الزيارة',
      },
      cameras: {
        created: 'تم إنشاء الكاميرا بنجاح',
        updated: 'تم تحديث الكاميرا بنجاح',
        deleted: 'تم حذف الكاميرا بنجاح',
        fetchFailed: 'فشل في جلب الكاميرات',
      },
      addresses: {
        created: 'تم إنشاء العنوان بنجاح',
        updated: 'تم تحديث العنوان بنجاح',
        deleted: 'تم حذف العنوان بنجاح',
        favoriteAdded: 'تمت إضافة العنوان إلى المفضلة',
        favoriteRemoved: 'تمت إزالة العنوان من المفضلة',
        setDefault: 'تم تحديث العنوان الافتراضي',
        fetchFailed: 'فشل في جلب العناوين',
        createFailed: 'فشل في إنشاء العنوان',
        updateFailed: 'فشل في تحديث العنوان',
        deleteFailed: 'فشل في حذف العنوان',
      },
      accommodations: {
        roomTypeCreated: 'تم إنشاء نوع الغرفة بنجاح',
        roomTypeUpdated: 'تم تحديث نوع الغرفة بنجاح',
        roomTypeDeleted: 'تم حذف نوع الغرفة بنجاح',
        roomCreated: 'تم إنشاء الغرفة بنجاح',
        roomUpdated: 'تم تحديث الغرفة بنجاح',
        roomDeleted: 'تم حذف الغرفة بنجاح',
        bookingCreated: 'تم إنشاء حجز الغرفة بنجاح',
        bookingCancelled: 'تم إلغاء حجز الغرفة',
        mealOptionCreated: 'تم إنشاء خيار الوجبة بنجاح',
        mealOptionUpdated: 'تم تحديث خيار الوجبة بنجاح',
        mealOptionDeleted: 'تم حذف خيار الوجبة بنجاح',
        approved: 'تمت الموافقة على الإقامة',
        rejected: 'تم رفض الإقامة',
        fetchFailed: 'فشل في جلب بيانات الإقامة',
        createFailed: 'فشل في إنشاء الإقامة',
        updateFailed: 'فشل في تحديث الإقامة',
        deleteFailed: 'فشل في حذف الإقامة',
        approveFailed: 'فشل في الموافقة على الإقامة',
        rejectFailed: 'فشل في رفض الإقامة',
      },
      incomingMaterials: {
        created: 'تم الإنشاء بنجاح',
        updated: 'تم تحديث الشحنة بنجاح',
        updateFailed: 'فشل في تحديث الشحنة',
        sentToAds: 'تم إرسال أمر الشراء إلى ADS',
      },
    },

    header: {
      // Factory selector
      selectFactory: 'اختر المصنع',
      allFactories: 'جميع المصانع',
      searchFactories: 'البحث عن المصانع...',
      // Search
      search: 'البحث في العقود والطلبات...',
      // User
      customer: 'عميل',
      admin: 'مشرف',
      profile: 'الملف الشخصي',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
    },

    // Contracts (used in sales orders and overview)
    contracts: {
      totalValue: 'القيمة الإجمالية',
      used: 'المستخدم',
      remaining: 'المتبقي',
      utilization: 'الاستخدام',
      lowStock: 'مخزون منخفض',
      draft: 'مسودة',
      expiringSoon: 'ينتهي قريباً',
      searchPlaceholder: 'بحث في العقود...',
      startDate: 'تاريخ البدء',
      endDate: 'تاريخ الانتهاء',
      orders: 'الطلبات',
      products: 'المنتجات',
      status: 'الحالة',
      active: 'نشط',
      completed: 'مكتمل',
      canceled: 'ملغي',
      duration: 'المدة',
      notes: 'ملاحظات',
      createdBy: 'أنشئ بواسطة',
    },

    sidebar: {
      // Main navigation
      overview: 'نظرة عامة',
      menu: 'القائمة',
      portal: 'بوابة العميل',
      logout: 'تسجيل الخروج',

      // Sales & Fulfillment
      salesFulfillment: 'المبيعات والتنفيذ',
      salesOrders: 'طلبات المبيعات',
      orders: 'الطلبات',
      customers: 'العملاء',
      contracts: 'العقود',
      shipments: 'الشحنات',
      incomingMaterials: 'المواد الواردة',
      logistics: 'اللوجستيات',
      procurement: 'المشتريات',
      fulfillment: 'التنفيذ',
      orderManagement: 'إدارة الطلبات',

      // Sales
      sales: 'المبيعات',
      salesDashboard: 'لوحة المبيعات',
      subCustomersDashboard: 'لوحة العملاء الفرعيين',
      customersDashboard: 'لوحة العملاء',
      salesTargets: 'الأهداف الشهرية',

      // Finance
      finance: 'المالية',
      statement: 'كشف الحساب',
      transactions: 'المعاملات',
      payments: 'المدفوعات',
      invoices: 'الفواتير',

      // Fleet
      fleet: 'الأسطول',
      resources: 'الموارد',
      drivers: 'السائقون',
      vehicles: 'المركبات',
      trailers: 'المقطورات',

      // Admin
      systemAdmin: 'إدارة النظام',
      adminDashboard: 'لوحة تحكم المشرف',
      tenants: 'المستأجرون',
      users: 'المستخدمون',
      customerAccounts: 'حسابات العملاء',
      factories: 'المصانع',
      regions: 'المناطق',
      cities: 'المدن',
      storageLocations: 'مواقع التخزين',
      products: 'المنتجات',
      availability: 'التوفر',

      // Catalog
      catalog: 'الكتالوج',
      pricing: 'التسعير',
      certificates: 'الشهادات',

      // Locations
      locations: 'المواقع',

      // Gate Operations
      gateOperations: 'عمليات البوابة',
      gates: 'البوابات',
      zones: 'المناطق',
      loadingLocations: 'مواقع التحميل',
      visitors: 'الزوار',
      cameras: 'الكاميرات',
      cameraSpeed: 'سرعة الكاميرا',
      qrReaders: 'قارئات QR',
      accessLogs: 'سجلات الوصول',
      departments: 'الأقسام',
      employees: 'الموظفون',
      accommodations: 'الإقامة',
      roomTypes: 'أنواع الغرف',
      mealOptions: 'خيارات الوجبات',

      // Organization
      organization: 'المنظمة',

      // System
      system: 'النظام',
      systemHealth: 'صحة النظام',
      sapSync: 'مزامنة SAP',
      sapConfiguration: 'تكوين SAP',
      visitGuidelines: 'إرشادات الزيارة',
      simulation: 'المحاكاة',
      controlTower: 'برج التحكم',
      yard: 'خريطة الساحة',
      notificationTemplates: 'قوالب الإشعارات',
      hardwareLogs: 'سجلات الأجهزة',
      portalConfig: 'إعدادات البوابة',
      financeOverview: 'نظرة عامة على المالية',
      assets: 'الأصول',
      dashboard: 'لوحة التحكم',
      help: 'المساعدة',

      // Secondary
      addressbook: 'دفتر العناوين',
      projects: 'المشاريع',

      // Support
      support: 'الدعم',
      supportTickets: 'تذاكر الدعم',

      // Bottom
      faq: 'الأسئلة الشائعة',
      settings: 'الإعدادات',

      // Kiosk
      scan: 'مسح الطلب',
      weighbridge: 'الميزان',

      // Accessibility
      closeMenu: 'إغلاق القائمة',
    },

    // Error boundary
    errorBoundary: {
      title: 'حدث خطأ ما',
      description: 'نأسف، حدث شيء غير متوقع. يرجى تحديث الصفحة أو العودة إلى الصفحة الرئيسية.',
      refreshPage: 'تحديث الصفحة',
      goHome: 'الصفحة الرئيسية',
      developerInfo: 'معلومات المطور',
      persistContact: 'إذا استمرت المشكلة، يرجى التواصل مع',
    },

    // SSO / Handoff
    sso: {
      signingIn: 'جاري تسجيل دخولك...',
      signInFailed: 'فشل تسجيل الدخول',
      goToLogin: 'الذهاب لتسجيل الدخول',
    },

    // Privacy Policy
    privacyPolicy: {
      backToSignIn: 'العودة إلى تسجيل الدخول',
      title: 'سياسة الخصوصية',
      lastUpdated: 'آخر تحديث:',
    },

    // Fleet Management
    fleet: {
      title: 'إدارة الأسطول',
      subtitle: 'إدارة السائقين والمركبات وتعيينات الموارد',
      resources: 'الموارد',
      drivers: 'السائقون',
      vehicles: 'المركبات',
      trailers: 'المقطورات',
      resourcesSubtitle: 'تعيينات السائقين والمركبات',
      driversSubtitle: 'إدارة السائقين',
      vehiclesSubtitle: 'إدارة أسطول المركبات',
      trailersSubtitle: 'إدارة المقطورات',
      search: 'بحث...',

      // Resources
      addResource: 'تعيين مورد',
      editResource: 'تعديل التعيين',
      noResources: 'لم يتم العثور على تعيينات موارد',
      noResourcesDescription: 'ابدأ بإنشاء أول تعيين مورد.',
      assignDriver: 'تعيين سائق',
      assignVehicle: 'تعيين مركبة',
      assignTrailer: 'تعيين مقطورة',
      assignedSince: 'معين منذ',
      currentShipment: 'الشحنة الحالية',
      resourceStatus: 'الحالة',
      resourceActive: 'نشط',
      resourceOnDelivery: 'قيد التوصيل',
      resourceReturning: 'عائد',
      resourceInactive: 'غير نشط',
      unassign: 'إلغاء التعيين',
      totalResources: 'إجمالي الموارد',
      activeResources: 'الموارد النشطة',
      onDelivery: 'قيد التوصيل',
      resourceName: 'اسم المورد',
      resourceDescription: 'الوصف',

      // Drivers
      addDriver: 'إضافة سائق',
      editDriver: 'تعديل السائق',
      deleteDriver: 'حذف السائق',
      noDrivers: 'لم يتم العثور على سائقين',
      noDriversDescription: 'ابدأ بإضافة أول سائق إلى الأسطول.',
      driverName: 'اسم السائق',
      driverNameAr: 'اسم السائق (بالعربية)',
      employeeId: 'رقم الموظف',
      licenseNumber: 'رقم الرخصة',
      licenseType: 'نوع الرخصة',
      licenseExpiry: 'انتهاء الرخصة',
      selectDate: 'اختر التاريخ',
      licenseExpiryRequired: 'تاريخ انتهاء الرخصة مطلوب.',
      licenseHeavy: 'مركبات ثقيلة',
      licenseLight: 'مركبات خفيفة',
      licenseHazmat: 'مواد خطرة',
      licenseSpecial: 'خاصة',
      phone: 'الهاتف',
      driverStatus: 'الحالة',
      driverAvailable: 'متاح',
      driverAssigned: 'معين',
      driverOnLeave: 'في إجازة',
      driverInactive: 'غير نشط',
      totalDrivers: 'إجمالي السائقين',
      availableDrivers: 'متاح',
      assignedDrivers: 'معين',
      photo: 'الصورة',

      // Vehicles
      addVehicle: 'إضافة مركبة',
      editVehicle: 'تعديل المركبة',
      deleteVehicle: 'حذف المركبة',
      noVehicles: 'لم يتم العثور على مركبات',
      noVehiclesDescription: 'ابدأ بإضافة أول مركبة إلى الأسطول.',
      plateNumber: 'رقم اللوحة',
      vehicleType: 'نوع المركبة',
      typeTruck: 'شاحنة',
      typeMixer: 'خلاط',
      typeTanker: 'صهريج',
      typeFlatbed: 'سطحة',
      typeCrane: 'رافعة',
      typeLoader: 'لودر',
      make: 'الشركة المصنعة',
      model: 'الموديل',
      makeModel: 'الشركة / الموديل',
      year: 'السنة',
      vin: 'رقم الهيكل',
      capacity: 'السعة',
      capacityUnit: 'الوحدة',
      tons: 'طن',
      cubicMeters: 'م³',
      vehicleStatus: 'الحالة',
      vehicleAvailable: 'متاح',
      vehicleAssigned: 'معين',
      vehicleMaintenance: 'صيانة',
      vehicleOutOfService: 'خارج الخدمة',
      ownership: 'الملكية',
      owned: 'مملوك',
      leased: 'مؤجر',
      rentedOut: 'مؤجر للغير',
      outsourced: 'مصدر خارجي',
      brokered: 'وسيط',
      shared: 'مشترك',
      subcontracted: 'موكول',
      lastMaintenance: 'آخر صيانة',
      nextMaintenance: 'الصيانة القادمة',
      configureColumns: 'الأعمدة',
      selectVisibleColumns: 'اختر الأعمدة المرئية',
      totalVehicles: 'إجمالي المركبات',
      availableVehicles: 'متاح',
      inMaintenance: 'قيد الصيانة',

      // Trailers
      addTrailer: 'إضافة مقطورة',
      editTrailer: 'تعديل المقطورة',
      deleteTrailer: 'حذف المقطورة',
      noTrailers: 'لم يتم العثور على مقطورات',
      noTrailersDescription: 'ابدأ بإضافة أول مقطورة إلى الأسطول.',
    },

    // Admin Dashboard
    admin: {
      dashboard: 'لوحة التحكم',
      dashboardDescription:
        'مركز الإدارة المركزي لجميع عمليات بوابة الجنوب',
      stats: {
        tenants: 'المستأجرون',
        activeTenants: 'المنظمات النشطة',
        users: 'المستخدمون',
        registeredUsers: 'المستخدمون المسجلون',
        orders: 'الطلبات',
        totalOrders: 'إجمالي الطلبات',
        revenue: 'الإيرادات',
        monthlyRevenue: 'هذا الشهر',
      },
      system: {
        fleetStatus: 'حالة الأسطول',
        activeVehicles: 'المركبات النشطة',
        inTransit: 'في الطريق',
        atFactory: 'في المصنع',
        maintenance: 'الصيانة',
        gateOperations: 'عمليات البوابة',
        activeGates: 'البوابات النشطة',
        todayEntries: 'دخول اليوم',
        todayExits: 'خروج اليوم',
        pendingVisitors: 'الزوار المعلقون',
        systemHealth: 'صحة النظام',
        uptime: 'وقت التشغيل هذا الشهر',
        apiLatency: 'تأخير API',
        dbHealth: 'قاعدة البيانات',
        cacheHitRate: 'معدل إصابة الذاكرة المؤقتة',
      },
      quickActions: 'الإجراءات السريعة',
      quickActionsDescription: 'المهام الإدارية الشائعة',
      actions: {
        newTenant: 'إضافة مستأجر',
        newUser: 'إضافة مستخدم',
        newProduct: 'إضافة منتج',
        newVehicle: 'إضافة مركبة',
      },
      availability: {
        matrix: 'مصفوفة التوفر',
        factory: 'المصنع',
        totalFactories: 'المصانع',
        totalVariants: 'المتغيرات',
        available: 'متوفر',
        unavailable: 'غير متوفر',
        coverage: 'التغطية',
        coverageDesc: 'من إجمالي التركيبات',
        noData: 'لا توجد مصانع أو متغيرات متاحة',
      },
      // Tenant Management
      tenantManagement: 'إدارة المستأجرين',
      tenantManagementDesc: 'إدارة المنظمات والوصول إليها',
      tenantManagementSubtitle: 'إدارة المنظمات والوصول إليها',
      createTenant: 'إنشاء مستأجر',
      totalTenants: 'إجمالي المستأجرين',
      pendingApproval: 'في انتظار الموافقة',
      activeTenants: 'المستأجرون النشطون',
      suspendedTenants: 'معلق',
      searchTenants: 'البحث عن المستأجرين...',
      noTenantsFound: 'لم يتم العثور على مستأجرين',
      noTenantsFoundDesc: 'حاول تعديل معايير البحث أو التصفية',
      viewDetails: 'عرض التفاصيل',
      edit: 'تعديل',
      approve: 'موافقة',
      approveForbidden: 'ليس لديك صلاحية للموافقة على العملاء، اطلب من المسؤول إضافة الصلاحية',
      suspend: 'تعليق',
      activate: 'تفعيل',
      active: 'نشط',
      pending: 'قيد الانتظار',
      suspended: 'معلق',
      inactive: 'غير نشط',
      b2b: 'أعمال لأعمال',
      ecommerce: 'التجارة الإلكترونية',
      status: 'الحالة',
      channel: 'القناة',
      tenants: {
        columns: {
          name: 'المستأجر',
          channel: 'القناة',
          customerGroup: 'مجموعة العملاء',
          users: 'المستخدمون',
          status: 'الحالة',
          created: 'تاريخ الإنشاء',
        },
      },
      // User Management
      userManagement: 'إدارة المستخدمين',
      userManagementSubtitle: 'إدارة حسابات المستخدمين والصلاحيات',
      totalUsers: 'إجمالي المستخدمين',
      activeUsers: 'المستخدمون النشطون',
      invitedUsers: 'المدعوون',
      blockedUsers: 'المحظورون',
      inactiveUsers: 'غير نشط',
      searchUsers: 'البحث عن المستخدمين...',
      users: {
        title: 'إدارة المستخدمين',
        subtitle: 'إدارة المستخدمين عبر جميع المستأجرين',
        subtitleTenant: 'إدارة المستخدمين داخل مستأجرك',
        addUser: 'إضافة مستخدم',
        addUserSuccess: 'تم إنشاء المستخدم بنجاح',
        addUserFailed: 'تعذر إنشاء المستخدم',
        columns: {
          name: 'الاسم',
          tenant: 'المستأجر',
          roleLabel: 'الدور',
          status: 'الحالة',
          created: 'تاريخ الإنشاء',
          lastLogin: 'آخر تسجيل دخول',
        },
        neverLoggedIn: 'أبدًا',
      },
      blocked: 'محظور',
      noUsersFound: 'لم يتم العثور على مستخدمين',
      noUsersFoundDesc: 'حاول تعديل معايير البحث أو التصفية',
      editUser: 'تعديل المستخدم',
      username: 'اسم المستخدم',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      organizationInfo: 'المنظمة',
      companyName: 'الشركة',
      roleManagement: 'الدور',
      activity: 'النشاط',
      lastActive: 'آخر نشاط',
      emailConfirmed: 'تم تأكيد البريد الإلكتروني',
      sendPasswordReset: 'إرسال رسالة إعادة تعيين كلمة المرور',
      resetPassword: 'إعادة تعيين كلمة المرور',
      filterByStatus: 'التصفية حسب الحالة',
      filterByRole: 'التصفية حسب الدور',
      filterByLastLogin: 'التصفية حسب آخر تسجيل دخول',
      lastLoginFilter: {
        last7Days: 'آخر 7 أيام',
        last30Days: 'آخر 30 يومًا',
        over30Days: 'لم يسجل الدخول منذ أكثر من 30 يومًا',
        never: 'لم يسجل الدخول أبدًا',
      },
      inactiveUser: 'تعطيل المستخدم',
      reactivate: 'إعادة التفعيل',
      blockUser: 'حظر المستخدم',
      unblockUser: 'إلغاء حظر المستخدم',
      roles: {
        superAdmin: 'مدير عام',
        admin: 'مدير',
        financeAdmin: 'مدير مالي',
        tenantAdmin: 'مدير المستأجر',
        tenantUser: 'مستخدم المستأجر',
        salesOfficeUser: 'مستخدم مكتب المبيعات',
        securityGuard: 'حارس أمن',
        safetyManager: 'مدير السلامة',
        servicesManager: 'مدير الخدمات',
        kiosk: 'كشك',
        employee: 'موظف',
        customer: 'عميل',
        customerOperator: 'مشغل العميل',
        scaleOperator: 'مشغل الميزان',
        scaleAdmin: 'مدير الميزان',
        packhouseManager: 'مدير المستودع',
        executive: 'تنفيذي',
        headOfSales: 'رئيس المبيعات',
        salesManager: 'مدير المبيعات',
        securityManager: 'مدير الأمن',
        it: 'تقنية المعلومات',
        visitorApprovalAdmin: 'مسؤول الموافقة على الزوار',
        scmAdmin: 'مسؤول سلسلة الإمداد',
        authenticated: 'موثّق',
        public: 'عام',
        ecommerce: 'التجارة الإلكترونية',
        manager: 'مدير',
        operator: 'مشغل',
        viewer: 'مشاهد',
      },
      user: {
        form: {
          addTitle: 'إضافة مستخدم',
          addDescription: 'إنشاء حساب مستخدم وتعيين مستوى الوصول',
          editDescription: 'تحديث ملف المستخدم وإعدادات الوصول',
          createUser: 'إنشاء المستخدم',
          userInfo: 'معلومات المستخدم',
          regenerate: 'إعادة توليد',
          roleAssignment: 'الدور والتعيين',
          selectRole: 'اختر الدور',
          name: 'الاسم الكامل',
          email: 'البريد الإلكتروني',
          password: 'كلمة المرور',
          phone: 'رقم الهاتف',
          roleLabel: 'الدور',
          plant: 'المصنع',
          department: 'القسم',
          tenant: 'المستأجر',
          namePlaceholder: 'أدخل الاسم الكامل',
          emailPlaceholder: 'user@example.com',
          passwordPlaceholder: 'أدخل كلمة المرور',
          phonePlaceholder: '512345678',
          selectPlant: 'اختر المصنع',
          allPlants: 'جميع المصانع',
          departmentPlaceholder: 'أدخل القسم',
          validation: {
            nameRequired: 'الاسم مطلوب',
            emailRequired: 'البريد الإلكتروني مطلوب',
            emailInvalid: 'أدخل بريدًا إلكترونيًا صالحًا',
            passwordRequired: 'كلمة المرور مطلوبة',
            passwordMin: 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
            roleRequired: 'الدور مطلوب',
            plantRequiredForRole: 'المصنع مطلوب للدور المحدد',
          },
          role: {
            kiosk: 'كشك',
            salesOfficeUser: 'مستخدم مكتب المبيعات',
            financeAdmin: 'مدير مالي',
            securityGuard: 'حارس أمن',
            safetyManager: 'مدير السلامة',
            servicesManager: 'مدير الخدمات',
            customerOperator: 'مشغل العميل',
            scaleOperator: 'مشغل الميزان',
            scaleAdmin: 'مدير الميزان',
            packhouseManager: 'مدير المستودع',
            scmAdmin: 'مسؤول سلسلة الإمداد',
          },
        },
      },
    },

    // Pagination
    pagination: {
      showing: 'عرض {{start}}-{{end}} من {{total}}',
      perPage: 'لكل صفحة',
      goToPage: 'انتقل إلى الصفحة {{page}}',
      previousPage: 'الصفحة السابقة',
      nextPage: 'الصفحة التالية',
    },

    // Timestamps
    timestamps: 'الطوابع الزمنية',
    createdAt: 'تاريخ الإنشاء',
    updatedAt: 'تاريخ التحديث',
    documentId: 'معرف المستند',

    // Additional common keys
    from: 'من',
    to: 'إلى',
    clearAll: 'مسح الكل',
    clearFilters: 'مسح الفلاتر',
    noOptions: 'لا توجد خيارات متاحة',
    shareUrl: 'مشاركة الرابط',
    tableView: 'عرض جدول',
    cardView: 'عرض بطاقات',
    duplicate: 'نسخ',
    last7Days: 'آخر 7 أيام',
    last30Days: 'آخر 30 يوم',
    presetRanges: 'النطاقات المحددة مسبقاً',

    shipments: {
      delivered: 'تم التسليم',
      loading: 'جاري التحميل',
      scheduled: 'مجدولة',
      quantity: 'الكمية',
      scheduledDate: 'تاريخ الجدولة',
    },
    assets: {
      resourceManagement: 'إدارة الموارد',
    },
  },

  ur: {
    appearance: {
      title: 'ظاہری شکل',
      subtitle: 'منتخب کریں کہ پورٹل کیسا دکھائی دے اور کیا دکھائے۔',
      display: {
        title: 'ڈسپلے',
        description: 'تھیم اور بصری انداز۔',
        theme: 'تھیم',
        themeDesc: 'پورٹل کے لیے بصری تھیم منتخب کریں۔',
        light: 'ہلکا',
        dark: 'گہرا',
      },
      dashboards: {
        title: 'ڈیش بورڈز',
        description: 'ڈیش بورڈ چارٹس کیا دکھاتے ہیں۔',
        hideFridays: 'جمعہ چھپائیں',
        hideFridaysDesc: 'روزانہ ڈیش بورڈ چارٹس سے جمعہ کے دن نکال دیں۔ مجموعے میں یہ ہمیشہ شامل ہوتے ہیں۔',
      },
    },
    common: {
      // Actions
      save: 'محفوظ کریں',
      saving: 'محفوظ ہو رہا ہے...',
      cancel: 'منسوخ کریں',
      edit: 'ترمیم کریں',
      delete: 'حذف کریں',
      close: 'بند کریں',
      export: 'ایکسپورٹ',
      share: 'شیئر کریں',
      sharing: 'شیئر ہو رہا ہے...',
      shareFileFailed: 'اس ڈیوائس پر فائل شیئر نہیں ہو سکی۔',
      retry: 'دوبارہ کوشش کریں',
      loading: 'لوڈ ہو رہا ہے...',
      search: 'تلاش کریں',
      filter: 'فلٹر',

      // Status
      all: 'تمام',
      active: 'فعال',
      inactive: 'غیر فعال',
      pending: 'زیر التواء',

      // Time
      today: 'آج',
      yesterday: 'کل',
      thisMonth: 'اس مہینے',
      lastMonth: 'پچھلے مہینے',
      thisYear: 'اس سال',
      lastYear: 'پچھلے سال',

      // Messages
      error: 'خرابی',
      success: 'کامیابی',
      comingSoon: 'جلد آ رہا ہے',
      optional: 'اختیاری',
      viewAll: 'سب دیکھیں',
      total: 'کل',
      noResults: 'کوئی نتائج نہیں ملے',
      actions: 'اقدامات',
      confirm: 'تصدیق کریں',
      back: 'واپس',
      next: 'اگلا',
      previous: 'پچھلا',
      submit: 'جمع کرائیں',
      create: 'بنائیں',
      update: 'اپ ڈیٹ کریں',
      add: 'شامل کریں',
      remove: 'ہٹائیں',
      select: 'منتخب کریں',
      clear: 'صاف کریں',
      reset: 'ری سیٹ کریں',
      apply: 'لاگو کریں',
      done: 'ہو گیا',
      min: 'کم سے کم',
      max: 'زیادہ سے زیادہ',
      noOptionsFound: 'کوئی آپشن نہیں ملا',
      saveChanges: 'تبدیلیاں محفوظ کریں',
      unknown: 'نامعلوم',
      copied: 'کاپی ہو گیا!',
      copy: 'کاپی کریں',
      creating: 'بنایا جا رہا ہے...',
      notAvailable: 'دستیاب نہیں',
      overview: 'جائزہ',
      other: 'دیگر',
      view: 'دیکھیں',
      filters: 'فلٹرز',
      filtersActive: 'فلٹرز فعال',
      tenant: 'کرایہ دار',
      period: 'مدت',
      ascending: 'صعودی',
      descending: 'نزولی',
      sort: 'ترتیب',
      clearAll: 'سب صاف کریں',
      clearFilters: 'فلٹرز صاف کریں',
      noOptions: 'کوئی آپشن دستیاب نہیں',
      from: 'سے',
      to: 'تک',
      noLimit: 'کوئی حد نہیں',
      transactions: 'لین دین',
      noTransactions: 'کوئی لین دین نہیں ملا',
      user: 'صارف',
      guest: 'مہمان',
      processing: 'پروسیسنگ...',
      date: 'تاریخ',
      yes: 'ہاں',
      no: 'نہیں',
      selectDateRange: 'تاریخ کی حد منتخب کریں',
      last7Days: 'آخری 7 دن',
      last30Days: 'آخری 30 دن',
      pendingChanges: 'زیر التواء تبدیلیاں',
      discard: 'منسوخ کریں',
      createdAt: 'بنانے کی تاریخ',
      updatedAt: 'اپ ڈیٹ کی تاریخ',
    },

    // Shared validation messages (used by driver drawer, forms, etc.)
    validation: {
      phoneSaudiFormat:
        '9 ہندسے درج کریں (0 کے بغیر) یا 10 ہندسے (0 کے ساتھ)۔ سعودی فارمیٹ: +966۔',
      phoneRequired: 'فون نمبر درکار ہے۔',
      driverIdMustBe10Digits: 'ڈرائیور آئی ڈی بالکل 10 ہندسے ہونی چاہیے۔',
      driverIdRequired: 'ڈرائیور آئی ڈی درکار ہے۔',
      driverIdFormat: 'ڈرائیور آئی ڈی بالکل 10 ہندسے ہونی چاہیے۔',
    },

    // Overview/Dashboard section
    overview: {
      welcome: 'خوش آمدید',
      summary: 'آج آپ کے اکاؤنٹ میں کیا ہو رہا ہے',
      recentOrders: 'حالیہ آرڈرز',
      recentActivity: 'آپ کی تازہ ترین آرڈر سرگرمی',
      viewAll: 'سب دیکھیں',
      contractUsage: 'معاہدہ استعمال',
      utilizationProgress: 'فعال معاہدوں کی استعمال کی پیش رفت',
      yearlyTrend: 'سالانہ رجحان',
    },

    // Stats section
    stats: {
      activeContracts: 'فعال معاہدے',
      totalOrders: 'کل آرڈرز',
      productsDelivered: 'فراہم کردہ مصنوعات',
      savings: 'بچت',
      thisMonth: 'اس مہینے',
      thisYear: 'اس سال',
      vsLastMonth: 'پچھلے مہینے کے مقابلے میں',
    },

    form: {
      name: 'نام',
      nameEn: 'نام (انگریزی)',
      nameAr: 'نام (عربی)',
      email: 'ای میل',
      phone: 'فون',
      address: 'پتہ',
      city: 'شہر',
      country: 'ملک',
      selectCountry: 'ملک منتخب کریں',
      selectCity: 'شہر منتخب کریں',
      postalCode: 'پوسٹل کوڈ',
      description: 'تفصیل',
      notes: 'نوٹس',
      status: 'حالت',

      validation: {
        required: 'یہ فیلڈ ضروری ہے',
        emailInvalid: 'براہ کرم درست ای میل پتہ درج کریں',
        phoneInvalid: 'براہ کرم درست فون نمبر درج کریں',
        minLength: 'کم از کم لمبائی پوری نہیں ہوئی',
        maxLength: 'زیادہ سے زیادہ لمبائی سے تجاوز',
      },
    },

    dateRangePicker: {
      placeholder: 'تاریخ کی حد منتخب کریں',
      presetsLabel: 'فوری انتخاب',
      startLabel: 'شروع',
      endLabel: 'اختتام',
      clearLabel: 'صاف کریں',
      selectDatePlaceholder: 'تاریخ منتخب کریں',
      today: 'آج',
      thisWeek: 'اس ہفتے',
      thisMonth: 'اس مہینے',
      last30Days: 'گزشتہ 30 دن',
      last90Days: 'گزشتہ 90 دن',
      thisQuarter: 'اس سہ ماہی',
      thisYear: 'اس سال',
      next30Days: 'اگلے 30 دن',
      next90Days: 'اگلے 90 دن',
    },

    toast: {
      success: 'کامیابی',
      error: 'خرابی',
      warning: 'انتباہ',
      info: 'معلومات',
      employees: {
        created: 'ملازم کامیابی سے بنایا گیا',
        updated: 'ملازم کامیابی سے اپ ڈیٹ ہوا',
        deleted: 'ملازم کامیابی سے حذف ہوا',
        fetchFailed: 'ملازمین لانے میں ناکامی',
        createFailed: 'ملازم بنانے میں ناکامی',
        updateFailed: 'ملازم اپ ڈیٹ کرنے میں ناکامی',
        deleteFailed: 'ملازم حذف کرنے میں ناکامی',
      },
      departments: {
        created: 'محکمہ کامیابی سے بنایا گیا',
        updated: 'محکمہ کامیابی سے اپ ڈیٹ ہوا',
        deleted: 'محکمہ کامیابی سے حذف ہوا',
        fetchFailed: 'محکمے لانے میں ناکامی',
        createFailed: 'محکمہ بنانے میں ناکامی',
        updateFailed: 'محکمہ اپ ڈیٹ کرنے میں ناکامی',
        deleteFailed: 'محکمہ حذف کرنے میں ناکامی',
      },
      visitors: {
        created: 'دورہ کامیابی سے بنایا گیا',
        checkedIn: 'زائر کامیابی سے چیک ان ہوا',
        checkedOut: 'زائر کامیابی سے چیک آؤٹ ہوا',
        approved: 'دورے کی کامیابی سے منظوری ہوئی',
        rejected: 'دورہ کامیابی سے مسترد ہوا',
        cancelled: 'دورہ کامیابی سے منسوخ ہوا',
        fetchFailed: 'دورے لانے میں ناکامی',
        createFailed: 'دورہ بنانے میں ناکامی',
        checkInFailed: 'زائر کا چیک ان ناکام',
        checkOutFailed: 'زائر کا چیک آؤٹ ناکام',
        approveFailed: 'دورے کی منظوری ناکام',
        cancelFailed: 'دورہ منسوخ کرنے میں ناکامی',
      },
      cameras: {
        created: 'کیمرا کامیابی سے بنایا گیا',
        updated: 'کیمرا کامیابی سے اپ ڈیٹ ہوا',
        deleted: 'کیمرا کامیابی سے حذف ہوا',
        fetchFailed: 'کیمرے لانے میں ناکامی',
      },
      addresses: {
        created: 'پتہ کامیابی سے بنایا گیا',
        updated: 'پتہ کامیابی سے اپ ڈیٹ ہوا',
        deleted: 'پتہ کامیابی سے حذف ہوا',
        favoriteAdded: 'پتہ پسندیدہ میں شامل ہوا',
        favoriteRemoved: 'پتہ پسندیدہ سے ہٹایا گیا',
        setDefault: 'پہلے سے طے شدہ پتہ اپ ڈیٹ ہوا',
        fetchFailed: 'پتے لانے میں ناکامی',
        createFailed: 'پتہ بنانے میں ناکامی',
        updateFailed: 'پتہ اپ ڈیٹ کرنے میں ناکامی',
        deleteFailed: 'پتہ حذف کرنے میں ناکامی',
      },
      accommodations: {
        roomTypeCreated: 'کمرے کی قسم کامیابی سے بنائی گئی',
        roomTypeUpdated: 'کمرے کی قسم کامیابی سے اپ ڈیٹ ہوئی',
        roomTypeDeleted: 'کمرے کی قسم کامیابی سے حذف ہوئی',
        roomCreated: 'کمرا کامیابی سے بنایا گیا',
        roomUpdated: 'کمرا کامیابی سے اپ ڈیٹ ہوا',
        roomDeleted: 'کمرا کامیابی سے حذف ہوا',
        bookingCreated: 'کمرے کی بکنگ کامیابی سے بنائی گئی',
        bookingCancelled: 'کمرے کی بکنگ منسوخ ہوئی',
        mealOptionCreated: 'کھانے کا آپشن کامیابی سے بنایا گیا',
        mealOptionUpdated: 'کھانے کا آپشن کامیابی سے اپ ڈیٹ ہوا',
        mealOptionDeleted: 'کھانے کا آپشن کامیابی سے حذف ہوا',
        approved: 'رہائش کی منظوری ہوئی',
        rejected: 'رہائش مسترد ہوئی',
        fetchFailed: 'رہائش کا ڈیٹا لانے میں ناکامی',
        createFailed: 'رہائش بنانے میں ناکامی',
        updateFailed: 'رہائش اپ ڈیٹ کرنے میں ناکامی',
        deleteFailed: 'رہائش حذف کرنے میں ناکامی',
        approveFailed: 'رہائش کی منظوری ناکام',
        rejectFailed: 'رہائش مسترد کرنے میں ناکامی',
      },
      incomingMaterials: {
        created: 'کامیابی سے بن گیا',
        updated: 'شپمنٹ کامیابی سے اپ ڈیٹ ہوئی',
        updateFailed: 'شپمنٹ اپ ڈیٹ کرنے میں ناکامی',
        sentToAds: 'خریداری آرڈر ADS کو بھیج دیا گیا',
      },
    },

    header: {
      // Factory selector
      selectFactory: 'فیکٹری منتخب کریں',
      allFactories: 'تمام فیکٹریاں',
      searchFactories: 'فیکٹریاں تلاش کریں...',
      // Search
      search: 'معاہدے، آرڈرز تلاش کریں...',
      // User
      customer: 'کسٹمر',
      admin: 'ایڈمن',
      profile: 'پروفائل',
      settings: 'ترتیبات',
      logout: 'لاگ آؤٹ',
    },

    // Contracts (used in sales orders and overview)
    contracts: {
      totalValue: 'کل قیمت',
      used: 'استعمال شدہ',
      remaining: 'باقی',
      utilization: 'استعمال',
      lowStock: 'کم اسٹاک',
      draft: 'مسودہ',
      expiringSoon: 'جلد ختم ہو رہا ہے',
      searchPlaceholder: 'معاہدے تلاش کریں...',
      startDate: 'شروع کی تاریخ',
      endDate: 'اختتام کی تاریخ',
      orders: 'آرڈرز',
      products: 'مصنوعات',
      status: 'حالت',
      active: 'فعال',
      completed: 'مکمل',
      canceled: 'منسوخ',
      duration: 'مدت',
      notes: 'نوٹس',
      createdBy: 'بنانے والا',
    },

    sidebar: {
      // Main navigation
      overview: 'جائزہ',
      menu: 'مینو',
      portal: 'کسٹمر پورٹل',
      logout: 'لاگ آؤٹ',

      // Sales & Fulfillment
      salesFulfillment: 'سیلز اور تکمیل',
      salesOrders: 'سیلز آرڈرز',
      orders: 'آرڈرز',
      customers: 'گاہک',
      contracts: 'معاہدے',
      shipments: 'شپمنٹس',
      incomingMaterials: 'آنے والا مواد',
      logistics: 'لاجسٹکس',
      procurement: 'خریداری',
      fulfillment: 'تکمیل',
      orderManagement: 'آرڈر مینجمنٹ',

      // Sales
      sales: 'سیلز',
      salesDashboard: 'سیلز ڈیش بورڈ',
      subCustomersDashboard: 'ذیلی کسٹمرز ڈیش بورڈ',
      customersDashboard: 'گاہکوں کا ڈیش بورڈ',
      salesTargets: 'ماہانہ اہداف',

      // Finance
      finance: 'مالیات',
      statement: 'بیان',
      transactions: 'لین دین',
      payments: 'ادائیگیاں',
      invoices: 'انوائسز',

      // Fleet
      fleet: 'فلیٹ',
      resources: 'وسائل',
      drivers: 'ڈرائیورز',
      vehicles: 'گاڑیاں',
      trailers: 'ٹریلرز',

      // Admin
      systemAdmin: 'سسٹم ایڈمنسٹریشن',
      adminDashboard: 'ایڈمن ڈیش بورڈ',
      tenants: 'کرایہ دار',
      users: 'صارفین',
      customerAccounts: 'کسٹمر اکاؤنٹس',
      factories: 'فیکٹریاں',
      regions: 'علاقے',
      cities: 'شہر',
      storageLocations: 'ذخیرہ مقامات',
      products: 'مصنوعات',
      availability: 'دستیابی',

      // Catalog
      catalog: 'کیٹلاگ',
      pricing: 'قیمتیں',
      certificates: 'سرٹیفیکیٹس',

      // Locations
      locations: 'مقامات',

      // Gate Operations
      gateOperations: 'گیٹ آپریشنز',
      gates: 'گیٹس',
      zones: 'زونز',
      loadingLocations: 'لوڈنگ مقامات',
      visitors: 'زائرین',
      cameras: 'کیمرے',
      cameraSpeed: 'کیمرہ کی رفتار',
      qrReaders: 'QR ریڈرز',
      accessLogs: 'رسائی لاگز',
      departments: 'محکمے',
      employees: 'ملازمین',
      accommodations: 'رہائش',
      roomTypes: 'کمرے کی اقسام',
      mealOptions: 'کھانے کے اختیارات',

      // Organization
      organization: 'تنظیم',

      // System
      system: 'سسٹم',
      systemHealth: 'سسٹم کی صحت',
      sapSync: 'SAP مطابقت',
      sapConfiguration: 'SAP ترتیب',
      visitGuidelines: 'دورے کی ہدایات',
      simulation: 'سمولیشن',
      controlTower: 'کنٹرول ٹاور',
      yard: 'یارڈ نقشہ',
      notificationTemplates: 'اطلاعاتی سانچے',
      hardwareLogs: 'ہارڈویئر لاگز',
      portalConfig: 'پورٹل ترتیبات',
      financeOverview: 'مالیات کا جائزہ',
      assets: 'اثاثے',
      dashboard: 'ڈیش بورڈ',
      help: 'مدد',

      // Secondary
      addressbook: 'ایڈریس بک',
      projects: 'پروجیکٹس',

      // Support
      support: 'سپورٹ',
      supportTickets: 'سپورٹ ٹکٹس',

      // Bottom
      faq: 'عمومی سوالات',
      settings: 'ترتیبات',

      // Kiosk
      scan: 'آرڈر اسکین',
      weighbridge: 'وزن پل',

      // Accessibility
      closeMenu: 'مینو بند کریں',
    },

    // Error boundary
    errorBoundary: {
      title: 'کچھ غلط ہو گیا',
      description: 'ہمیں افسوس ہے، کچھ غیر متوقع ہوا۔ براہ کرم صفحہ تازہ کریں یا ہوم پیج پر جائیں۔',
      refreshPage: 'صفحہ تازہ کریں',
      goHome: 'گھر جائیں',
      developerInfo: 'ڈویلپر معلومات',
      persistContact: 'اگر یہ مسئلہ جاری رہے تو رابطہ کریں',
    },

    // SSO / Handoff
    sso: {
      signingIn: 'آپ کو سائن ان کیا جا رہا ہے...',
      signInFailed: 'سائن ان ناکام',
      goToLogin: 'لاگ ان پر جائیں',
    },

    // Privacy Policy
    privacyPolicy: {
      backToSignIn: 'سائن ان پر واپس جائیں',
      title: 'رازداری کی پالیسی',
      lastUpdated: 'آخری تازہ کاری:',
    },

    // Fleet Management
    fleet: {
      title: 'فلیٹ مینجمنٹ',
      subtitle: 'ڈرائیورز، گاڑیوں اور وسائل کی تفویض کا انتظام کریں',
      resources: 'وسائل',
      drivers: 'ڈرائیورز',
      vehicles: 'گاڑیاں',
      trailers: 'ٹریلرز',
      resourcesSubtitle: 'ڈرائیور اور گاڑی کی تفویض',
      driversSubtitle: 'اپنے ڈرائیورز کا انتظام کریں',
      vehiclesSubtitle: 'اپنے گاڑیوں کے فلیٹ کا انتظام کریں',
      trailersSubtitle: 'اپنے ٹریلرز کا انتظام کریں',
      search: 'تلاش کریں...',

      // Resources
      addResource: 'وسائل تفویض کریں',
      editResource: 'تفویض میں ترمیم کریں',
      noResources: 'کوئی وسائل کی تفویض نہیں ملی',
      noResourcesDescription: 'اپنی پہلی وسائل کی تفویض بنا کر شروع کریں۔',
      assignDriver: 'ڈرائیور تفویض کریں',
      assignVehicle: 'گاڑی تفویض کریں',
      assignTrailer: 'ٹریلر تفویض کریں',
      assignedSince: 'تفویض کی تاریخ',
      currentShipment: 'موجودہ شپمنٹ',
      resourceStatus: 'حالت',
      resourceActive: 'فعال',
      resourceOnDelivery: 'ڈیلیوری پر',
      resourceReturning: 'واپس آ رہا ہے',
      resourceInactive: 'غیر فعال',
      unassign: 'تفویض ہٹائیں',
      totalResources: 'کل وسائل',
      activeResources: 'فعال وسائل',
      onDelivery: 'ڈیلیوری پر',
      resourceName: 'وسائل کا نام',
      resourceDescription: 'تفصیل',

      // Drivers
      addDriver: 'ڈرائیور شامل کریں',
      editDriver: 'ڈرائیور میں ترمیم کریں',
      deleteDriver: 'ڈرائیور حذف کریں',
      noDrivers: 'کوئی ڈرائیور نہیں ملا',
      noDriversDescription: 'فلیٹ میں اپنا پہلا ڈرائیور شامل کر کے شروع کریں۔',
      driverName: 'ڈرائیور کا نام',
      driverNameAr: 'ڈرائیور کا نام (عربی)',
      employeeId: 'ملازم آئی ڈی',
      licenseNumber: 'لائسنس نمبر',
      licenseType: 'لائسنس کی قسم',
      licenseExpiry: 'لائسنس کی میعاد',
      selectDate: 'تاریخ منتخب کریں',
      licenseExpiryRequired: 'لائسنس کی میعاد ختم ہونے کی تاریخ ضروری ہے۔',
      licenseHeavy: 'بھاری گاڑی',
      licenseLight: 'ہلکی گاڑی',
      licenseHazmat: 'خطرناک مواد',
      licenseSpecial: 'خصوصی',
      phone: 'فون',
      driverStatus: 'حالت',
      driverAvailable: 'دستیاب',
      driverAssigned: 'تفویض شدہ',
      driverOnLeave: 'چھٹی پر',
      driverInactive: 'غیر فعال',
      totalDrivers: 'کل ڈرائیورز',
      availableDrivers: 'دستیاب',
      assignedDrivers: 'تفویض شدہ',
      photo: 'تصویر',

      // Vehicles
      addVehicle: 'گاڑی شامل کریں',
      editVehicle: 'گاڑی میں ترمیم کریں',
      deleteVehicle: 'گاڑی حذف کریں',
      noVehicles: 'کوئی گاڑی نہیں ملی',
      noVehiclesDescription: 'فلیٹ میں اپنی پہلی گاڑی شامل کر کے شروع کریں۔',
      plateNumber: 'لائسنس پلیٹ',
      vehicleType: 'گاڑی کی قسم',
      typeTruck: 'ٹرک',
      typeMixer: 'مکسر',
      typeTanker: 'ٹینکر',
      typeFlatbed: 'فلیٹ بیڈ',
      typeCrane: 'کرین',
      typeLoader: 'لوڈر',
      make: 'ساخت',
      model: 'ماڈل',
      makeModel: 'ساخت / ماڈل',
      year: 'سال',
      vin: 'وی آئی این',
      capacity: 'گنجائش',
      capacityUnit: 'یونٹ',
      tons: 'ٹن',
      cubicMeters: 'مکعب میٹر',
      vehicleStatus: 'حالت',
      vehicleAvailable: 'دستیاب',
      vehicleAssigned: 'تفویض شدہ',
      vehicleMaintenance: 'دیکھ بھال',
      vehicleOutOfService: 'سروس سے باہر',
      ownership: 'ملکیت',
      owned: 'ملکیتی',
      leased: 'لیز پر',
      rentedOut: 'کرایے پر دیا گیا',
      outsourced: 'آؤٹ سورس',
      brokered: 'بروکر شدہ',
      shared: 'مشترکہ',
      subcontracted: 'ذیلی معاہدہ',
      lastMaintenance: 'آخری دیکھ بھال',
      nextMaintenance: 'اگلی دیکھ بھال',
      configureColumns: 'کالم',
      selectVisibleColumns: 'نظر آنے والے کالم منتخب کریں',
      totalVehicles: 'کل گاڑیاں',
      availableVehicles: 'دستیاب',
      inMaintenance: 'دیکھ بھال میں',

      // Trailers
      addTrailer: 'ٹریلر شامل کریں',
      editTrailer: 'ٹریلر میں ترمیم کریں',
      deleteTrailer: 'ٹریلر حذف کریں',
      noTrailers: 'کوئی ٹریلر نہیں ملا',
      noTrailersDescription: 'فلیٹ میں اپنا پہلا ٹریلر شامل کر کے شروع کریں۔',
    },

    // Admin Dashboard
    admin: {
      dashboard: 'ایڈمن ڈیش بورڈ',
      dashboardDescription: 'تمام بوابة الجنوب آپریشنز کے لیے مرکزی انتظامی مرکز',
      stats: {
        tenants: 'کرایہ دار',
        activeTenants: 'فعال تنظیمیں',
        users: 'صارفین',
        registeredUsers: 'رجسٹرڈ صارفین',
        orders: 'آرڈرز',
        totalOrders: 'کل آرڈرز',
        revenue: 'آمدنی',
        monthlyRevenue: 'اس مہینے',
      },
      system: {
        fleetStatus: 'فلیٹ کی حالت',
        activeVehicles: 'فعال گاڑیاں',
        inTransit: 'راستے میں',
        atFactory: 'فیکٹری میں',
        maintenance: 'دیکھ بھال',
        gateOperations: 'گیٹ آپریشنز',
        activeGates: 'فعال گیٹس',
        todayEntries: 'آج کے داخلے',
        todayExits: 'آج کے اخراج',
        pendingVisitors: 'زیر التواء زائرین',
        systemHealth: 'سسٹم کی صحت',
        uptime: 'اس مہینے اپ ٹائم',
        apiLatency: 'API تاخیر',
        dbHealth: 'ڈیٹا بیس',
        cacheHitRate: 'کیش ہٹ ریٹ',
      },
      quickActions: 'فوری اقدامات',
      quickActionsDescription: 'عام انتظامی کام',
      actions: {
        newTenant: 'کرایہ دار شامل کریں',
        newUser: 'صارف شامل کریں',
        newProduct: 'پروڈکٹ شامل کریں',
        newVehicle: 'گاڑی شامل کریں',
      },
      availability: {
        matrix: 'دستیابی میٹرکس',
        factory: 'فیکٹری',
        totalFactories: 'فیکٹریاں',
        totalVariants: 'ویریئنٹس',
        available: 'دستیاب',
        unavailable: 'دستیاب نہیں',
        coverage: 'کوریج',
        coverageDesc: 'کل مجموعوں میں سے',
        noData: 'کوئی فیکٹری یا ویریئنٹ دستیاب نہیں',
      },
      // Tenant Management
      tenantManagement: 'کرایہ دار کا انتظام',
      tenantManagementDesc: 'تنظیموں اور ان کی رسائی کا انتظام',
      tenantManagementSubtitle: 'تنظیموں اور ان کی رسائی کا انتظام',
      createTenant: 'کرایہ دار بنائیں',
      totalTenants: 'کل کرایہ دار',
      pendingApproval: 'منظوری کا انتظار',
      activeTenants: 'فعال کرایہ دار',
      suspendedTenants: 'معطل',
      searchTenants: 'کرایہ داروں کی تلاش...',
      noTenantsFound: 'کوئی کرایہ دار نہیں ملا',
      noTenantsFoundDesc: 'تلاش یا فلٹر کے معیار کو ایڈجسٹ کرنے کی کوشش کریں',
      viewDetails: 'تفصیلات دیکھیں',
      edit: 'ترمیم',
      approve: 'منظور کریں',
      approveForbidden: 'آپ کو گاہک منظور کرنے کی اجازت نہیں ہے، منتظم سے اجازت شامل کرنے کی درخواست کریں',
      suspend: 'معطل کریں',
      activate: 'فعال کریں',
      active: 'فعال',
      pending: 'زیر التواء',
      suspended: 'معطل',
      inactive: 'غیر فعال',
      b2b: 'بی ٹو بی',
      ecommerce: 'ای کامرس',
      status: 'حالت',
      channel: 'چینل',
      tenants: {
        columns: {
          name: 'کرایہ دار',
          channel: 'چینل',
          customerGroup: 'کسٹمر گروپ',
          users: 'صارفین',
          status: 'حالت',
          created: 'بنانے کی تاریخ',
        },
      },
      // User Management
      userManagement: 'صارف کا انتظام',
      userManagementSubtitle: 'صارف اکاؤنٹس اور اجازتوں کا انتظام',
      totalUsers: 'کل صارفین',
      activeUsers: 'فعال صارفین',
      invitedUsers: 'مدعو',
      blockedUsers: 'مسدود',
      inactiveUsers: 'غیر فعال',
      searchUsers: 'صارفین تلاش کریں...',
      users: {
        title: 'صارف کا انتظام',
        subtitle: 'تمام کرایہ داروں میں صارفین کا انتظام',
        subtitleTenant: 'اپنے کرایہ دار میں صارفین کا انتظام کریں',
        addUser: 'صارف شامل کریں',
        addUserSuccess: 'صارف کامیابی سے بنا دیا گیا',
        addUserFailed: 'صارف بنانے میں ناکامی',
        columns: {
          name: 'نام',
          tenant: 'کرایہ دار',
          roleLabel: 'کردار',
          status: 'حالت',
          created: 'بنانے کی تاریخ',
          lastLogin: 'آخری لاگ ان',
        },
        neverLoggedIn: 'کبھی نہیں',
      },
      blocked: 'مسدود',
      noUsersFound: 'کوئی صارف نہیں ملا',
      noUsersFoundDesc: 'اپنی تلاش یا فلٹر کے معیار کو تبدیل کریں',
      editUser: 'صارف میں ترمیم کریں',
      username: 'صارف نام',
      email: 'ای میل',
      phone: 'فون',
      organizationInfo: 'تنظیم',
      companyName: 'کمپنی',
      roleManagement: 'کردار',
      activity: 'سرگرمی',
      lastActive: 'آخری سرگرمی',
      emailConfirmed: 'ای میل کی تصدیق ہو چکی ہے',
      sendPasswordReset: 'پاس ورڈ ری سیٹ ای میل بھیجیں',
      resetPassword: 'پاس ورڈ ری سیٹ کریں',
      filterByStatus: 'حالت کے لحاظ سے فلٹر کریں',
      filterByRole: 'کردار کے لحاظ سے فلٹر کریں',
      filterByLastLogin: 'آخری لاگ ان کے لحاظ سے فلٹر کریں',
      lastLoginFilter: {
        last7Days: 'پچھلے 7 دن',
        last30Days: 'پچھلے 30 دن',
        over30Days: '30 دن سے زیادہ سے لاگ ان نہیں',
        never: 'کبھی لاگ ان نہیں کیا',
      },
      inactiveUser: 'صارف کو غیر فعال کریں',
      reactivate: 'دوبارہ فعال کریں',
      blockUser: 'صارف کو بلاک کریں',
      unblockUser: 'صارف کو ان بلاک کریں',
      roles: {
        superAdmin: 'سپر ایڈمن',
        admin: 'ایڈمن',
        financeAdmin: 'فنانس ایڈمن',
        tenantAdmin: 'کرایہ دار ایڈمن',
        tenantUser: 'کرایہ دار صارف',
        salesOfficeUser: 'سیلز آفس صارف',
        securityGuard: 'سیکیورٹی گارڈ',
        safetyManager: 'سیفٹی مینیجر',
        servicesManager: 'سروسز مینیجر',
        kiosk: 'کیوسک',
        employee: 'ملازم',
        customer: 'گاہک',
        customerOperator: 'گاہک آپریٹر',
        scaleOperator: 'اسکیل آپریٹر',
        scaleAdmin: 'اسکیل ایڈمن',
        packhouseManager: 'پیک ہاؤس مینیجر',
        executive: 'ایگزیکٹو',
        headOfSales: 'سیلز کے سربراہ',
        salesManager: 'سیلز مینیجر',
        securityManager: 'سیکیورٹی مینیجر',
        it: 'آئی ٹی',
        visitorApprovalAdmin: 'وزیٹر اپروول ایڈمن',
        scmAdmin: 'ایس سی ایم ایڈمن',
        authenticated: 'تصدیق شدہ',
        public: 'عوامی',
        ecommerce: 'ای کامرس',
        manager: 'مینیجر',
        operator: 'آپریٹر',
        viewer: 'ویور',
      },
      user: {
        form: {
          addTitle: 'صارف شامل کریں',
          addDescription: 'صارف اکاؤنٹ بنائیں اور رسائی تفویض کریں',
          editDescription: 'صارف پروفائل اور رسائی کی ترتیبات اپ ڈیٹ کریں',
          createUser: 'صارف بنائیں',
          userInfo: 'صارف کی معلومات',
          regenerate: 'دوبارہ بنائیں',
          roleAssignment: 'کردار اور تفویض',
          selectRole: 'کردار منتخب کریں',
          name: 'پورا نام',
          email: 'ای میل ایڈریس',
          password: 'پاس ورڈ',
          phone: 'فون نمبر',
          roleLabel: 'کردار',
          plant: 'پلانٹ',
          department: 'شعبہ',
          tenant: 'کرایہ دار',
          namePlaceholder: 'پورا نام درج کریں',
          emailPlaceholder: 'user@example.com',
          passwordPlaceholder: 'پاس ورڈ درج کریں',
          phonePlaceholder: '512345678',
          selectPlant: 'پلانٹ منتخب کریں',
          allPlants: 'تمام پلانٹس',
          departmentPlaceholder: 'شعبہ درج کریں',
          validation: {
            nameRequired: 'نام درکار ہے',
            emailRequired: 'ای میل درکار ہے',
            emailInvalid: 'درست ای میل درج کریں',
            passwordRequired: 'پاس ورڈ درکار ہے',
            passwordMin: 'پاس ورڈ کم از کم 6 حروف کا ہونا چاہیے',
            roleRequired: 'کردار درکار ہے',
            plantRequiredForRole: 'منتخب کردار کے لیے پلانٹ درکار ہے',
          },
          role: {
            kiosk: 'کیوسک',
            salesOfficeUser: 'سیلز آفس صارف',
            financeAdmin: 'فنانس ایڈمن',
            securityGuard: 'سیکیورٹی گارڈ',
            safetyManager: 'سیفٹی مینیجر',
            servicesManager: 'سروسز مینیجر',
            customerOperator: 'گاہک آپریٹر',
            scaleOperator: 'اسکیل آپریٹر',
            scaleAdmin: 'اسکیل ایڈمن',
            packhouseManager: 'پیک ہاؤس مینیجر',
            scmAdmin: 'ایس سی ایم ایڈمن',
          },
        },
      },
    },

    // Pagination
    pagination: {
      showing: '{{start}}-{{end}} دکھا رہا ہے {{total}} میں سے',
      perPage: 'فی صفحہ',
      goToPage: 'صفحہ {{page}} پر جائیں',
      previousPage: 'پچھلا صفحہ',
      nextPage: 'اگلا صفحہ',
    },

    // Timestamps
    timestamps: 'ٹائم اسٹیمپس',
    createdAt: 'بنانے کی تاریخ',
    updatedAt: 'اپ ڈیٹ کی تاریخ',
    documentId: 'دستاویز آئی ڈی',

    // Additional common keys
    from: 'سے',
    to: 'تک',
    clearAll: 'سب صاف کریں',
    clearFilters: 'فلٹرز صاف کریں',
    noOptions: 'کوئی آپشن دستیاب نہیں',
    shareUrl: 'یو آر ایل شیئر کریں',
    tableView: 'ٹیبل ویو',
    cardView: 'کارڈ ویو',
    duplicate: 'نقل کریں',
    last7Days: 'آخری 7 دن',
    last30Days: 'آخری 30 دن',
    presetRanges: 'پہلے سے طے شدہ رینجز',

    shipments: {
      delivered: 'ڈیلیور کر دیا گیا',
      loading: 'لوڈنگ',
      scheduled: 'مجدول',
      quantity: 'مقدار',
      scheduledDate: 'مجدول تاریخ',
    },
    assets: {
      resourceManagement: 'وسائل کا انتظام',
    },
  },
};

// Separate exports for portal compatibility
export const coreTranslationsEn = coreTranslations.en;
export const coreTranslationsAr = coreTranslations.ar;
export const coreTranslationsUr = coreTranslations.ur;
